import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2, XCircle, Loader2, Inbox, ExternalLink, FileText, ShieldCheck, Stethoscope, Fingerprint,
  BadgeCheck, Briefcase, GraduationCap, FileBadge, RefreshCw, MessageSquare, User, X, AlertTriangle, Clock,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { SecureFileLink } from './SecureFileLink';
import { DocumentAccessLog } from './DocumentAccessLog';

const SOURCE_LABEL = { legacy_certificate: 'Certificate wallet', identity_verification: 'ID verification', training_certificate: 'Training engine' };

const CATEGORY_META = {
  identity: { label: 'Identity', icon: Fingerprint, cls: 'bg-sky-50 text-sky-700' },
  authorization: { label: 'Work authorisation', icon: Briefcase, cls: 'bg-violet-50 text-violet-700' },
  tax: { label: 'Tax', icon: FileText, cls: 'bg-slate-100 text-slate-700' },
  medical: { label: 'Medical', icon: Stethoscope, cls: 'bg-rose-50 text-rose-700' },
  certification: { label: 'Certification', icon: BadgeCheck, cls: 'bg-emerald-50 text-emerald-700' },
  training: { label: 'Training', icon: GraduationCap, cls: 'bg-indigo-50 text-indigo-700' },
  background: { label: 'Background check', icon: ShieldCheck, cls: 'bg-amber-50 text-amber-800' },
  employment: { label: 'Employment', icon: FileBadge, cls: 'bg-teal-50 text-teal-700' },
  other: { label: 'Other', icon: FileText, cls: 'bg-slate-100 text-slate-700' },
};

export function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function fmtSize(b) {
  if (!b) return '';
  return b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
}
export function expiryMeta(iso) {
  if (!iso) return { label: 'No expiry', cls: 'bg-slate-100 text-slate-600', icon: Clock };
  const days = Math.ceil((new Date(iso) - Date.now()) / 86400000);
  if (days < 0) return { label: `Expired ${fmtDate(iso)}`, cls: 'bg-red-100 text-red-700', icon: AlertTriangle };
  if (days <= 7) return { label: `Expires in ${days}d`, cls: 'bg-orange-100 text-orange-800', icon: AlertTriangle };
  if (days <= 30) return { label: `Expires in ${days}d`, cls: 'bg-amber-100 text-amber-800', icon: Clock };
  return { label: `Valid until ${fmtDate(iso)}`, cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 };
}

/** Document Review queue — HR sees every submitted credential, previews the file, and approves / rejects with a note. */
export default function HRISDocumentReview({ onOpenWorker }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [workerFilter, setWorkerFilter] = useState('all');
  const [drawerWorker, setDrawerWorker] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall('/api/hris/admin/review-queue?limit=200');
      setQueue(r.queue || []);
    } catch (e) { toast.error(e?.message || 'Could not load queue'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (row, decision, note) => {
    setBusy(`${row._id}:${decision}`);
    try {
      await apiCall(`/api/hris/admin/workers/${encodeURIComponent(row.worker_id)}/documents/${encodeURIComponent(row._id)}/review`,
        { method: 'POST', body: JSON.stringify({ decision, note: note || '' }) });
      toast.success(decision === 'approve' ? `${row.display_name} approved` : `${row.display_name} rejected — worker asked to re-upload`);
      await load();
    } catch (e) { toast.error(e?.message || 'Review failed'); }
    setBusy(null);
  };

  const workers = Object.values(queue.reduce((acc, r) => { acc[r.worker_id] = acc[r.worker_id] || { id: r.worker_id, name: r.worker_name, n: 0 }; acc[r.worker_id].n += 1; return acc; }, {}));
  const rows = workerFilter === 'all' ? queue : queue.filter(r => r.worker_id === workerFilter);
  const expiringSoon = queue.filter(r => r.expires_at && (new Date(r.expires_at) - Date.now()) < 30 * 86400000).length;

  if (loading && queue.length === 0) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="hris-document-review">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Awaiting review" value={queue.length} cls="bg-indigo-50 text-indigo-800" testid="doc-kpi-pending" />
        <Kpi label="Workers with submissions" value={workers.length} cls="bg-slate-100 text-slate-800" testid="doc-kpi-workers" />
        <Kpi label="Expiring ≤ 30 days" value={expiringSoon} cls={expiringSoon ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-800'} testid="doc-kpi-expiring" />
      </div>

      {queue.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center" data-testid="admin-hris-queue-empty">
          <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-600 font-medium text-sm">Inbox zero</p>
          <p className="text-slate-400 text-xs">No documents waiting for review. New uploads from workers land here automatically.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
            <Inbox className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900 text-sm">Documents awaiting review ({rows.length})</h3>
            <select value={workerFilter} onChange={e => setWorkerFilter(e.target.value)} className="ml-auto text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" data-testid="doc-filter-worker">
              <option value="all">All workers</option>
              {workers.map(w => <option key={w.id} value={w.id}>{w.name} ({w.n})</option>)}
            </select>
            <button onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh" data-testid="doc-refresh"><RefreshCw className="w-4 h-4" /></button>
          </div>
          <ul className="divide-y divide-slate-100">
            {rows.map(row => (
              <DocRow key={row._id} row={row} busy={busy} onReview={review} onOpenWorker={(id) => setDrawerWorker({ id, name: row.worker_name })} />
            ))}
          </ul>
        </div>
      )}
      {drawerWorker && <WorkerDocsDrawer worker={drawerWorker} onClose={() => setDrawerWorker(null)} onChanged={load} />}
    </div>
  );
}

function Kpi({ label, value, cls, testid }) {
  return <div className={`rounded-xl p-3 ${cls}`} data-testid={testid}><p className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</p><p className="text-2xl font-bold">{value}</p></div>;
}

function DocRow({ row, busy, onReview, onOpenWorker }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const meta = CATEGORY_META[row.category] || CATEGORY_META.other;
  const Icon = meta.icon;
  const exp = expiryMeta(row.expires_at);
  const ExpIcon = exp.icon;
  const isBusy = !!busy;
  return (
    <li className="px-4 py-4" data-testid={`admin-hris-queue-${row._id}`}>
      <div className="flex items-start gap-3">
        <span className={`p-2.5 rounded-xl ${meta.cls}`}><Icon className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900">{row.display_name}</p>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
            <span className="text-[10px] font-mono text-slate-400">{row.subtype}</span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">v{row.version_number || 1}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-slate-500">
            <button onClick={() => onOpenWorker(row.worker_id)} className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:underline" data-testid={`doc-worker-${row._id}`}>
              <User className="w-3 h-3" /> {row.worker_name}
            </button>
            {row.worker_pending_count > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{row.worker_pending_count} pending</span>}
            <span>·</span><span>submitted {fmtDate(row.updated_at)}</span>
            {row.file_name && <><span>·</span><span className="truncate max-w-[200px]">{row.file_name}</span>{row.size_bytes ? <span className="text-slate-400">({fmtSize(row.size_bytes)})</span> : null}</>}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${exp.cls}`} data-testid={`doc-expiry-${row._id}`}><ExpIcon className="w-3 h-3" /> {exp.label}</span>
            {row.file_url ? (
              <SecureFileLink path={row.file_url} workerId={row.worker_id} documentId={row._id} sensitivity={row.sensitivity_level}
                fileLabel={`${row.worker_name} — ${row.display_name || row.subtype}`} testId={`doc-view-${row._id}`} />
            ) : row.external_url ? (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${row.external_url}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid={`doc-view-${row._id}`}>
                <ExternalLink className="w-3 h-3" /> View certificate
              </a>
            ) : <span className="text-[11px] text-slate-400 italic">No file attached (metadata only)</span>}
            {SOURCE_LABEL[row.source] && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`doc-source-${row._id}`}>via {SOURCE_LABEL[row.source]}</span>}
            <button onClick={() => setNoteOpen(o => !o)} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${noteOpen || note ? 'border-indigo-300 text-indigo-700 bg-indigo-50' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-testid={`doc-note-toggle-${row._id}`}>
              <MessageSquare className="w-3 h-3" /> {note ? 'Note added' : 'Add note'}
            </button>
          </div>
          {noteOpen && (
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={500}
              placeholder="Review note for the worker — e.g. 'Photo is blurry, please re-upload a clear scan' or 'Verified against issuer registry'"
              className="mt-2 w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
              data-testid={`doc-note-${row._id}`} />
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch gap-2 flex-shrink-0">
          <button onClick={() => onReview(row, 'approve', note)} disabled={isBusy}
            className="inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid={`admin-hris-queue-approve-${row._id}`}>
            {busy === `${row._id}:approve` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
          </button>
          <button onClick={() => { if (!note.trim()) { setNoteOpen(true); toast.info('Add a short note so the worker knows what to fix'); return; } onReview(row, 'reject', note); }} disabled={isBusy}
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            data-testid={`admin-hris-queue-reject-${row._id}`}>
            {busy === `${row._id}:reject` ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} Reject
          </button>
        </div>
      </div>
    </li>
  );
}

const STATE_CLS = { submitted: 'bg-blue-100 text-blue-800', in_review: 'bg-blue-100 text-blue-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-700', draft: 'bg-slate-100 text-slate-600' };

function WorkerDocsDrawer({ worker, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => apiCall(`/api/hris/admin/workers/${worker.id}/hr-profile`).then(setData).catch(e => toast.error(e.message)), [worker.id]);
  useEffect(() => { load(); }, [load]);

  const review = async (doc, decision) => {
    const note = decision === 'reject' ? (window.prompt('Why is this document rejected? (sent to the worker)') || '') : '';
    if (decision === 'reject' && !note.trim()) return;
    setBusy(doc._id);
    try {
      await apiCall(`/api/hris/admin/workers/${worker.id}/documents/${doc._id}/review`, { method: 'POST', body: JSON.stringify({ decision, note }) });
      toast.success(`Document ${decision}d`); await load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
    setBusy(null);
  };

  const docs = data?.documents || [];
  const elig = data?.eligibility;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/50 backdrop-blur-sm" data-testid="worker-docs-drawer">
      <div className="w-full max-w-xl bg-white h-full shadow-2xl flex flex-col">
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-indigo-300">Worker compliance file</p>
            <h3 className="text-xl font-bold" data-testid="drawer-worker-name">{worker.name}</h3>
            {elig && (
              <span className={`inline-flex items-center gap-1 mt-2 text-xs font-semibold px-2 py-0.5 rounded-full ${elig.eligible ? 'bg-emerald-500/20 text-emerald-200' : 'bg-red-500/20 text-red-200'}`} data-testid="drawer-eligibility">
                {elig.eligible ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {elig.eligible ? 'Eligible for shifts' : `Not eligible · ${elig.code}`}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="drawer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {data && <DocumentAccessLog workerId={worker.id} />}
          {!data ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            : docs.length === 0 ? <p className="text-sm text-slate-400 text-center py-10">No documents on file.</p>
            : docs.map(d => {
              const meta = CATEGORY_META[d.category] || CATEGORY_META.other; const Icon = meta.icon; const exp = expiryMeta(d.expires_at);
              return (
                <div key={d._id} className="border border-slate-200 rounded-xl p-3" data-testid={`drawer-doc-${d._id}`}>
                  <div className="flex items-start gap-3">
                    <span className={`p-2 rounded-lg ${meta.cls}`}><Icon className="w-4 h-4" /></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">{d.display_name || d.subtype}</p>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATE_CLS[d.review_state] || STATE_CLS.draft}`}>{(d.review_state || 'draft').replace('_', ' ')}</span>
                        <span className="text-[10px] text-slate-400">v{d.version_number || 1}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">{meta.label} · {d.subtype} · updated {fmtDate(d.updated_at)}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${exp.cls}`}>{exp.label}</span>
                        {d.file_url && <SecureFileLink path={d.file_url} workerId={worker.id} documentId={d._id} sensitivity={d.sensitivity_level} fileLabel={d.display_name || d.subtype}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 hover:underline" testId={`drawer-view-${d._id}`} />}
                        {!d.file_url && d.external_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${d.external_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 hover:underline"><ExternalLink className="w-3 h-3" /> View certificate</a>}
                        {SOURCE_LABEL[d.source] && <span className="text-[10px] text-slate-500">via {SOURCE_LABEL[d.source]}</span>}
                      </div>
                      {d.review_note && <p className="text-[11px] text-slate-600 mt-1.5 bg-slate-50 rounded px-2 py-1">Note: {d.review_note}</p>}
                    </div>
                    {['submitted', 'in_review'].includes(d.review_state) && (
                      <div className="flex flex-col gap-1">
                        <button onClick={() => review(d, 'approve')} disabled={busy === d._id} className="text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid={`drawer-approve-${d._id}`}>Approve</button>
                        <button onClick={() => review(d, 'reject')} disabled={busy === d._id} className="text-[11px] font-semibold px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50" data-testid={`drawer-reject-${d._id}`}>Reject</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
